import * as React from 'react';
import {useSelector} from 'react-redux';

import {useRive} from '@rive-app/react-canvas';

import {Session} from 'nutshell-core/session';

import {Microsoft365RegularIcon} from 'shells/icon/svg/microsoft-365-regular-icon';
import {PageHeaderFull} from 'shells/layout/page-header-full';
import {Stack} from 'shells/layout/stack/stack';
import GoogleLogoSvg from 'shells/logos/google-g.svg';
import {Body} from 'shells/typography/body';

import type {UserEmailCalendarStatusesFragment} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../store';

import {useSetGoogleMailSendIsEnabled} from '../../setup/email/graphql/hooks/use-set-google-mail-send-is-enabled';
import {useSetOffice365MailSendIsEnabled} from '../../setup/email/graphql/hooks/use-set-office365-mail-send-is-enabled';
import {InitialButton} from '../../setup/integrations/initial-button';

import {getSubtitle, getWelcomeMessage} from './helpers';
import type {ExplorePageVariant} from './types';

import styles from './explore-page.module.css';

interface Props {
    variant: ExplorePageVariant;
    userEmailCalendarStatuses: UserEmailCalendarStatusesFragment | null | undefined;
    onEmailCalendarAuthSuccess: (provider: 'google' | 'microsoft') => void;
}

export function ExplorePageHeader(props: Props) {
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);

    const {RiveComponent} = useRive({
        src: '/include/rive/trial-gathering/explore-header.riv',
        autoplay: true,
    });
    const loggedInUser = useSelector((state: NutshellState) => Session.getSessionStateUser(state));
    const userFirstName = loggedInUser && loggedInUser.firstName;

    const {enableGoogle} = useSetGoogleMailSendIsEnabled();
    const {enableO365} = useSetOffice365MailSendIsEnabled();

    const welcomeMessage = getWelcomeMessage(props.variant, userFirstName);
    const subtitle = getSubtitle(props.variant);

    const showConnectButtons =
        props.userEmailCalendarStatuses &&
        props.userEmailCalendarStatuses.sendableEmailAddresses.length === 0 &&
        !props.userEmailCalendarStatuses.calendarSync;

    return (
        <PageHeaderFull
            tabTitle='Explore'
            title={welcomeMessage}
            subtitle={subtitle}
            illustration={
                <div className={styles['header-illustration-container']}>
                    <RiveComponent style={{width: 350, height: 350}} />
                </div>
            }
            maxWidth={1200}
            additionalContent={
                showConnectButtons && (
                    <Stack spacing={16}>
                        <Body color='blue' isBold={true}>
                            Next step: Connect your email & calendar
                        </Body>
                        <Stack isHorizontal={true} spacing={16}>
                            <InitialButton
                                initialText='Connect to Google'
                                authCheckUrl='/google/has-auth'
                                windowFeatures={{
                                    toolbar: 'no',
                                    location: 'no',
                                    width: 600,
                                    height: 850,
                                }}
                                logo={<GoogleLogoSvg width={20} />}
                                enable={enableGoogle}
                                onAuthSuccess={() => {
                                    props.onEmailCalendarAuthSuccess('google');

                                    return Promise.resolve();
                                }}
                                onAuthError={(errMsgs) => setErrorMessages(errMsgs)}
                                isFullWidth={true}
                                centerContent={true}
                                compact={true}
                            />
                            <InitialButton
                                initialText='Connect to Microsoft 365'
                                authCheckUrl='/office365/has-auth'
                                windowFeatures={{
                                    toolbar: 'no',
                                    location: 'no',
                                    width: 600,
                                    height: 500,
                                }}
                                logo={<Microsoft365RegularIcon size={20} wrapWithDiv={false} />}
                                enable={enableO365}
                                onAuthSuccess={() => {
                                    props.onEmailCalendarAuthSuccess('microsoft');

                                    return Promise.resolve();
                                }}
                                onAuthError={(errMsgs) => setErrorMessages(errMsgs)}
                                isFullWidth={true}
                                centerContent={true}
                                compact={true}
                            />
                        </Stack>
                        {errorMessages.length > 0 && (
                            <Body color='error'>{errorMessages.join(', ')}</Body>
                        )}
                    </Stack>
                )
            }
        />
    );
}
